import type { Logo, LogoShellContext } from "./logo/Logo";
import { CubeLogo } from "./logo/cubeLogo";
import { isMobile } from "./util/mobile";

/**
 * シェル: 複数のロゴモーションを 1 ページで切り替えて検証する土台。
 *
 * - 単一の RAF ループ + FPS 計測 (全ロゴ共通の HUD)
 * - `[` / `]` キーでアクティブなロゴを差し替え
 * - 各ロゴは Logo インターフェースに従い、mount で自前の canvas / DOM / GUI を
 *   生成し unmount で破棄する
 */

const logoRoot = document.querySelector<HTMLDivElement>("#logo-root");
const logoNameEl = document.querySelector<HTMLSpanElement>("#logo-name");
const fpsEl = document.querySelector<HTMLSpanElement>("#fps");
if (!logoRoot) throw new Error("#logo-root not found");

// モバイルは cube 固定なので TypographyLogo / ParticleLogo を初期バンドルに含めない。
// Vite が動的 import を自動で別チャンクに分割するので、モバイルではそれらの
// chunk が一切ダウンロードされず数百 KB 軽くなる。PC は従来通りで揃ってから初期化。
const logos: Logo[] = [new CubeLogo()];
let activeIdx = -1;
let activeLogo: Logo | null = null;
let logoNames: string[] = logos.map((l) => l.name);

function switchLogo(idx: number): void {
  const next = ((idx % logos.length) + logos.length) % logos.length;
  if (next === activeIdx) return;
  activeLogo?.unmount();
  activeIdx = next;
  activeLogo = logos[activeIdx];
  // 各ロゴが自前の GUI にロゴ切替セレクタを置けるよう shell context を渡す。
  const shell: LogoShellContext = {
    logoNames,
    activeName: activeLogo.name,
    switchTo: (name: string) => {
      const i = logos.findIndex((l) => l.name === name);
      if (i >= 0) switchLogo(i);
    },
  };
  activeLogo.mount(logoRoot!, shell);
  if (logoNameEl) logoNameEl.textContent = activeLogo.name;
}

/**
 * フォーム要素 (input / textarea / select / contentEditable) にフォーカスが
 * あるとき true。タイムラインの名前入力などに文字を打っても、ロゴ切替や
 * 各ロゴのキーボードショートカットが誤発火しないようにするためのガード。
 */
function isTypingTarget(e: Event): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return (
    t.tagName === "INPUT" ||
    t.tagName === "TEXTAREA" ||
    t.tagName === "SELECT" ||
    t.isContentEditable
  );
}

window.addEventListener("keydown", (e) => {
  if (isTypingTarget(e)) return;
  // モバイルでは cube に固定し、intro4 + 指での回転だけのデモに絞る。
  // ロゴ切替は無効化 (HUD ボタンも mobile-hide、GUI もアクセス不可)。
  if (e.key === "/" && !isMobile()) {
    e.preventDefault();
    switchLogo(activeIdx + 1);
    return;
  }
  // H: GUI パネル / HUD 等のオーバーレイをすべて一括で表示・非表示。
  // ロゴ本体 (canvas / label-layer / typo wordmark) はそのまま見える。
  if (e.key === "h" || e.key === "H") {
    e.preventDefault();
    document.body.classList.toggle("hide-overlays");
  }
});

// HUD の「/ : switch logo」をクリックでも次のロゴへ切り替えられるように。
document
  .querySelector<HTMLButtonElement>("#switch-logo")
  ?.addEventListener("click", () => switchLogo(activeIdx + 1));

// PC では typography / particle を await してから初期マウント (GUI dropdown に
// 全ロゴが揃った状態で起動)。モバイルでは cube だけで即時マウント。
if (isMobile()) {
  switchLogo(0);
} else {
  void (async (): Promise<void> => {
    const [typoMod, partMod] = await Promise.all([
      import("./logo/typographyLogo"),
      import("./logo/particleLogo"),
    ]);
    logos.push(new typoMod.TypographyLogo());
    logos.push(new partMod.ParticleLogo());
    logoNames = logos.map((l) => l.name);
    switchLogo(0);
  })();
}

let last = performance.now() / 1000;
let fpsAcc = 0;
let fpsCount = 0;
let fpsLastEmit = last;

function frame(nowMs: number): void {
  const now = nowMs / 1000;
  const dt = Math.min(now - last, 1 / 30);
  last = now;

  activeLogo?.update(dt);

  fpsAcc += 1 / dt;
  fpsCount += 1;
  if (now - fpsLastEmit > 0.5 && fpsEl) {
    fpsEl.textContent = `${(fpsAcc / fpsCount).toFixed(0)} fps`;
    fpsAcc = 0;
    fpsCount = 0;
    fpsLastEmit = now;
  }

  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);
